import { Add, Remove } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid2,
  Popover,
  Typography,
  useTheme,
} from "@mui/material";
import { ControlledSelectField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-select-field";
import { ControlledTextFormField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-text-field";
import { FC, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { CustomFieldDefinition, ExtendedFieldError } from "vmd-types";

import { CATEGORY_OPTION_DEFAULT_COLOR } from "../../constants/colors";

/**
 * An entry for semaphore status with three threshold colors.
 */
export interface CustomizableFormFieldEntry {
  name: string;
  type: "Text" | "Number" | "Date" | "Select";
  color?: string;
  label: string;
}

export interface CustomizableFormFieldProps {
  field: CustomFieldDefinition;
  onItemUpdate?: (item: CustomFieldDefinition) => void;
  validationErrors: Record<string, ExtendedFieldError>;
  index: number;
}

export const CustomizableFormField: FC<CustomizableFormFieldProps> = ({
  index,
  field,
  onItemUpdate,
  validationErrors,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const notifyChange = (newEntries: { name: string; color?: string }[]) => {
    onItemUpdate?.({ ...field, options: newEntries });
  };

  const handleNameChange = (index: number, name: string) => {
    const updated = [...(field.options ?? [])];
    updated[index] = { ...updated[index], name };
    notifyChange(updated);
  };

  const handleColorChange = (index: number, color: string) => {
    const updated = [...(field.options ?? [])];
    updated[index].color = color;
    notifyChange(updated);
  };

  const addEntry = () => {
    if ((field.options ?? []).length < 5) {
      notifyChange([
        ...(field.options ?? []),
        {
          name: `option ${(field.options?.length ?? 0) + 1}`,
          color: CATEGORY_OPTION_DEFAULT_COLOR,
        },
      ]);
    }
  };

  const removeEntry = (index: number) => {
    const updated = [...(field.options ?? [])];
    updated.splice(index, 1);
    notifyChange(updated);
  };

  const togglePicker = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) => {
    if (openIndex === index) {
      setOpenIndex(null);
      setAnchorEl(null);
    } else {
      setOpenIndex(index);
      setAnchorEl(event.currentTarget);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        width: "100%",
        border: "1px solid #E0E0E0",
        padding: 2,
        borderRadius: "12px",
        flexDirection: "column",
      }}
    >
      <Grid2 container alignItems="center" spacing={2} size={12}>
        <Grid2 size={6}>
          <Typography sx={{ fontWeight: 500, fontSize: 14, width: "100%" }}>
            {"Label"}
          </Typography>
          <ControlledTextFormField
            value={field.label ?? ""}
            onChange={(val) => onItemUpdate?.({ ...field, label: val })}
            error={Boolean(validationErrors?.[`customFields.${index}.label`])}
            helperText={
              validationErrors?.[`customFields.${index}.label`]?.message
            }
          />
        </Grid2>

        <Grid2 size={6}>
          <Typography sx={{ fontWeight: 500, width: "100%", fontSize: 14 }}>
            {"Type"}
          </Typography>
          <ControlledSelectField
            options={
              [
                { label: "Text" },
                { label: "Number" },
                { label: "Date" },
                { label: "Select" },
              ]?.map((category) => ({
                label: category.label,
                value: category.label,
              })) ?? []
            }
            value={field.type ?? ""}
            onChange={(val) => {
              onItemUpdate?.({
                ...field,
                type: val as "Text" | "Number" | "Date" | "Select",
              });
            }}
          ></ControlledSelectField>
        </Grid2>
      </Grid2>
      {field.type === "Select" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography sx={{ fontWeight: 500, width: 280, fontSize: 14 }}>
            {"Options"}
          </Typography>
          {field.options?.map((entry, optionIndex) => (
            <Box
              key={optionIndex}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 5,
                alignItems: "center",
              }}
            >
              <Box sx={{ width: 360 }}>
                <ControlledTextFormField
                  value={entry.name}
                  onChange={(val) => handleNameChange(optionIndex, val)}
                  error={Boolean(
                    validationErrors?.[
                      `customFields.${index}.options.${optionIndex}.name`
                    ]
                  )}
                  helperText={
                    validationErrors?.[
                      `customFields.${index}.options.${optionIndex}.name`
                    ]?.message
                  }
                />
              </Box>

              <Box sx={{ display: "flex", gap: 4 }}>
                <Box>
                  <Box
                    onClick={(e) => togglePicker(e, index)}
                    sx={{
                      height: 30,
                      width: 30,
                      background: entry.color,
                      "&:hover": {
                        background: `${entry.color ?? CATEGORY_OPTION_DEFAULT_COLOR}50`,
                      },
                      borderRadius: "50%",
                    }}
                  />
                  <Popover
                    open={openIndex === index}
                    anchorEl={anchorEl}
                    onClose={() => setOpenIndex(null)}
                    sx={{ zIndex: 20000, borderRadius: "12px" }}
                  >
                    <HexColorPicker
                      color={entry.color}
                      onChange={(color) => handleColorChange(index, color)}
                    />
                  </Popover>
                </Box>
              </Box>

              <Button
                onClick={() => removeEntry(index)}
                sx={{
                  color: theme.palette.primary.dark,
                  fontSize: 30,
                  ml: 5,
                }}
              >
                <Remove />
              </Button>
            </Box>
          ))}
          <Box width={"50%"}>
            {(field.options?.length ?? 0) < 5 && (
              <Button
                onClick={addEntry}
                sx={{
                  color: theme.palette.primary.dark,
                  fontSize: 30,
                  width: "100%",
                }}
              >
                <Add />
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
